<template>
  <div class="border-top mt-1 pt-1 small-text">
    <VitalSignsSummary :readings="readings" />
    <div v-if="ackDt" class="text-success">
      ✓ รับทราบก่อนรักษา · {{ ackDoctorName }} {{ time(ackDt) }}
    </div>
    <div v-if="healthReviewDt" class="text-success">
      ประวัติสุขภาพ: ✓ {{ reviewLabel }}
      <template v-if="sourceLabel">({{ sourceLabel }})</template>
      <template v-if="healthUpdatedDt">
        · อัปเดตล่าสุด {{ day(healthUpdatedDt) }}</template
      >
    </div>
    <div v-else-if="statusId === 5" class="text-danger">ประวัติสุขภาพ: ยังไม่ได้ทบทวน</div>
    <b-link v-if="!adding" class="small-text" @click="adding = true"
      >+ วัด vital</b-link
    >
    <div v-else class="mt-1">
      <VitalSignsForm v-model="draft" :id-prefix="idPrefix" />
      <div class="d-flex justify-content-end mt-1">
        <b-button size="sm" variant="link" @click="cancel">ยกเลิก</b-button>
        <b-button
          size="sm"
          variant="outline-info"
          :disabled="isSaving"
          @click="save"
          >บันทึก</b-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { eventBus } from "@/main";
import VitalSignsForm from "@/components/aaci/VitalSignsForm";
import VitalSignsSummary from "@/components/aaci/VitalSignsSummary";
import { SOURCE_LABELS } from "@/utils/healthReview";
import { emptyReading, pendingReadings } from "@/utils/vitalSigns";

export default {
  name: "CheckinVitalsPanel",
  components: { VitalSignsForm, VitalSignsSummary },
  props: {
    appointmentId: { type: Number, default: null },
    readings: { type: Array, default: () => [] },
    ackDt: { type: [String, Date], default: null },
    ackDoctorName: { type: String, default: "" },
    idPrefix: { type: String, default: "ph" },
    healthReviewDt: { type: [String, Date], default: null },
    healthReviewSource: { type: String, default: null },
    healthUpdatedDt: { type: [String, Date], default: null },
    statusId: { type: Number, default: null },
  },
  data() {
    return { adding: false, draft: [emptyReading()], isSaving: false };
  },
  computed: {
    reviewLabel() {
      const m = moment(this.healthReviewDt);
      return m.isSame(moment(), "day")
        ? `ทบทวนวันนี้ ${m.format("HH:mm")}`
        : `ทบทวน ${this.day(this.healthReviewDt)} ${m.format("HH:mm")}`;
    },
    sourceLabel() {
      return SOURCE_LABELS[this.healthReviewSource] || "";
    },
  },
  methods: {
    day(dt) {
      return dt ? moment(dt).locale("th").format("D MMM YY") : "";
    },
    time(dt) {
      return dt ? moment(dt).format("HH:mm") : "";
    },
    cancel() {
      this.adding = false;
      this.draft = [emptyReading()];
    },
    async save() {
      const { readings, error } = pendingReadings(this.draft);
      const message = readings.length
        ? error
        : "กรุณากรอก vital signs อย่างน้อย 1 ค่า";
      if (message) {
        eventBus.$emit("alertToast", { message, variant: "warning" });
        return;
      }
      await this.persist(readings);
    },
    /**
     * Called by the parent before it saves the treatment, so vitals typed in
     * but not saved aren't lost.
     * @returns {Promise<boolean>} false when the values are invalid or the save failed
     */
    async savePendingVitals() {
      if (!this.adding) return true;
      const { readings, error } = pendingReadings(this.draft);
      if (!readings.length) return true;
      if (error) {
        eventBus.$emit("alertToast", { message: error, variant: "warning" });
        return false;
      }
      return this.persist(readings);
    },
    async persist(readings) {
      this.isSaving = true;
      try {
        const res = await this.$store.dispatch(
          "moduleAppointment/addVitalSigns",
          {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            appointmentId: this.appointmentId,
            readings,
            source: "TREATMENT",
          }
        );
        this.$emit("update:readings", res.data);
        this.cancel();
        return true;
      } catch (err) {
        eventBus.$emit("alertToast", {
          message:
            err.response?.data?.errorMessage || "บันทึก vital signs ไม่สำเร็จ",
          variant: "danger",
        });
        return false;
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
